import React from 'react';
import { throttle } from 'throttle-debounce';
import { useIntl } from 'react-intl';
import Html from 'components/html';
import useMobileView from 'enhancers/use-mobile-view';
import useResizeObserver from 'enhancers/use-resize-observer';
import Page from 'pages/layout/page';
import AppHeaderControls from 'pages/shared/app-header-controls';
import application from 'services/application';
import authentication from 'services/authentication';
import ff from 'services/feature-toggle';
import math from 'utils/math';

import VideoDreamsBanner from './video-dreams-banner';
import BecomeRichBanner from './become-rich-banner';
import WhyJoinBanner from './why-join-banner';
import StartEarning from './start-earning';
import CategoryBanner from './category-banner';
import YourContentBanner from './your-content-banner';
import WhatYouGetBanner from './what-you-get-banner';
import SuccessStoriesBanner from './success-stories-banner';
import QuestionsBanner from './questions-banner';
import NotConvincedBanner from './not-convinced';
import OnlineSupport from './online-support';
import { config as notConvincedBannerSettings } from './not-convinced/not-convinced-banner.settings';
import { banner1 as categoryBanner1, banner2 as categoryBanner2 } from './category-banner/category-banner.settings';
import type { CategoryCardElement } from './category-banner/category-card';
import messages from './landing.i18n';
import { config, testId } from './landing.settings';
import styles from './landing.module.scss';

const Landing: React.FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const isMobileView = useMobileView();
  const questionsRef = React.useRef<HTMLElement>(undefined);
  const stickyScrollContainerRef = React.useRef<HTMLDivElement>(null);
  const stickyScrollContentRef = React.useRef<HTMLDivElement>(null);
  const [stickyScrollPosition, setStickyScrollPosition] = React.useState<
    { upper: number; lower: number } | undefined
  >();
  const [curtainProgress, setCurtainProgress] = React.useState(0);
  const currentProduct = application.current;

  const updateStickyScrollPosition = React.useCallback(
    throttle(
      50,
      (): void => {
        if (!stickyScrollContainerRef.current || !stickyScrollContentRef.current) return;

        stickyScrollContentRef.current.classList.remove(styles.sticky);
        stickyScrollContainerRef.current.style.height = '';

        // The sticky scroll container will be a multiple in height of the content it contains. When sticky scroll
        // is active, the content will be fixed in place while the user scrolls the empty container behind the scenes.
        const componentSize = stickyScrollContainerRef.current.clientHeight;
        const headerOffset =
          (isMobileView && config.notConvincedSticky.headerOffsetMobile) || config.notConvincedSticky.headerOffset;
        const upper = stickyScrollContainerRef.current.offsetTop - headerOffset;
        const lower = upper + componentSize;
        const scrollSize = componentSize * config.notConvincedSticky.scrollMultiplier;

        stickyScrollContainerRef.current.style.height = `${scrollSize}px`;

        setStickyScrollPosition({ upper, lower });
      },
      { noLeading: true }
    ),
    []
  );

  React.useLayoutEffect(() => {
    updateStickyScrollPosition();
  }, [updateStickyScrollPosition]);

  // This banner expands vertically, so we need to update sticky scroll coordinates when it changes size.
  useResizeObserver(questionsRef, updateStickyScrollPosition);

  const handleWindowScroll = React.useCallback(
    throttle(10, (): void => {
      if (
        !stickyScrollPosition?.upper ||
        !stickyScrollPosition?.lower ||
        !stickyScrollContainerRef.current ||
        !stickyScrollContentRef.current
      )
        return;

      const { max, min } = notConvincedBannerSettings.curtainProgress;
      const scrollTop = window.scrollY;

      if (scrollTop > stickyScrollPosition.upper && scrollTop < stickyScrollPosition.lower) {
        // We are inside the sticky scroll region, so fix the banner in place and animate the curtain
        stickyScrollContentRef.current.classList.add(styles.sticky);

        const progress =
          ((scrollTop - stickyScrollPosition.upper) / (stickyScrollPosition.lower - stickyScrollPosition.upper)) * 100;

        setCurtainProgress(math.clamp(progress, min, max));

        return;
      }

      // We are outside the sticky scroll region
      stickyScrollContentRef.current.classList.remove(styles.sticky);

      if (scrollTop > stickyScrollPosition.lower) {
        stickyScrollContainerRef.current.classList.add(styles.scrolledBelow);
        stickyScrollContainerRef.current.classList.remove(styles.scrolledAbove);

        setCurtainProgress(max);

        return;
      }

      stickyScrollContainerRef.current.classList.add(styles.scrolledAbove);
      stickyScrollContainerRef.current.classList.remove(styles.scrolledBelow);
      setCurtainProgress(min);
    }),
    [stickyScrollPosition?.upper, stickyScrollPosition?.lower]
  );

  React.useLayoutEffect(() => {
    handleWindowScroll();

    window.addEventListener('scroll', handleWindowScroll);

    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [handleWindowScroll]);

  const categoryCards1 = React.useMemo(() => {
    return categoryBanner1.map(({ src, category, name, nameVariant }) => {
      return {
        src,
        category: formatMessage(category),
        name,
        nameVariant,
      } as CategoryCardElement;
    });
  }, [formatMessage]);

  const categoryCards2 = React.useMemo(() => {
    return categoryBanner2.map(({ src, category, name, nameVariant }) => {
      return {
        src,
        category: formatMessage(category),
        name,
        nameVariant,
      } as CategoryCardElement;
    });
  }, [formatMessage]);

  return (
    <Page
      testId={testId.page}
      seo={{
        title: formatMessage(messages.pages.landing.seo[currentProduct].title),
        description: formatMessage(messages.pages.landing.seo[currentProduct].title),
      }}
      size="fullscreen"
    >
      {!authentication.isAuthenticated() && <AppHeaderControls login signup />}
      <Page.Row spaceless className={[styles.landing, 'p-0']}>
        <Html.div className="w-100">
          <VideoDreamsBanner />
          <BecomeRichBanner />
          <WhyJoinBanner />
          <StartEarning />
          <CategoryBanner cards={categoryCards1} />
          <YourContentBanner />
          <CategoryBanner cards={categoryCards2} />
          <WhatYouGetBanner />
          <SuccessStoriesBanner />
          <Html.div ref={questionsRef}>
            <QuestionsBanner />
          </Html.div>
          <Html.div className={styles.stickyScrollContainer} ref={stickyScrollContainerRef}>
            <Html.div className="w-100" ref={stickyScrollContentRef}>
              <NotConvincedBanner curtainProgress={curtainProgress} />
            </Html.div>
          </Html.div>
        </Html.div>
        {ff.enabled('enableLandingChat') && <OnlineSupport />}
      </Page.Row>
    </Page>
  );
};

export default Landing;
